import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../../firebase';
import TonyteModal, { type TonyteModalButton } from '../../components/TonyteModal';
import { useI18n } from '../../i18n';
import { navigationRef } from '../../navigation/notificationNav';
import { notifyDayVibesChanged } from '../../hooks/useDayVibes';
import { showTonyteToast } from '../../utils/tonyteToast';
import { claimVibeWebSession } from './vibeClaimApi';
import { decideFromError, decideFromResult, type VibeClaimDecision, type VibeClaimNotice } from './vibeClaimOutcome';
import { checkInstallReferrerOnce } from './installReferrer';
import {
  clearPendingVibeClaim,
  loadPendingVibeClaim,
  subscribePendingVibeClaim,
  type PendingVibeClaim,
} from './pendingVibeClaim';
import type { VibeClaimCredential } from './vibeClaimLinks';

// Claims web Vibe game answers into the signed-in account.
//
// `ready` is AppNavigator's isMainStackMounted: signed in, profile loaded,
// onboarding finished, email verified — i.e. the DayVibes screens exist and
// the server's own eligibility checks will pass. Until then a pending claim
// just waits in storage (pendingVibeClaim.ts), across restarts, sign-in,
// registration and onboarding. Transient failures keep it and retry on the
// next foreground; only a final server answer (or the user) clears it.

const RETRY_AFTER_MS = 20_000;
const EMAIL_DISMISSED_KEY = 'tonyte.vibeClaim.emailDismissed.v1';
const EMAIL_INDEX_COLLECTION = 'vibeWebSessionEmails';

type Props = { ready: boolean; userId: string | null };

function formatDateKey(dateKey: string, language: string) {
  try {
    return new Intl.DateTimeFormat(language, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
      .format(new Date(`${dateKey}T12:00:00Z`));
  } catch {
    return dateKey;
  }
}

async function readDismissedEmailSessions(): Promise<string[]> {
  try {
    const raw = await AsyncStorage.getItem(EMAIL_DISMISSED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(item => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

export default function VibeClaimCoordinator({ ready, userId }: Props) {
  const { t, language } = useI18n();
  const [pending, setPending] = useState<PendingVibeClaim | null>(null);
  const [notice, setNotice] = useState<VibeClaimNotice | null>(null);
  const [emailOffer, setEmailOffer] = useState<{ sessionId: string; email: string } | null>(null);
  const [foregroundTick, setForegroundTick] = useState(0);
  const inFlight = useRef(false);
  const lastTransientFailureAt = useRef(0);
  const emailCheckedFor = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;
    loadPendingVibeClaim().then(value => {
      if (mounted) setPending(value);
    });
    const unsubscribe = subscribePendingVibeClaim(setPending);
    checkInstallReferrerOnce().catch(() => {});
    const appStateSub = AppState.addEventListener('change', status => {
      if (status === 'active') setForegroundTick(tick => tick + 1);
    });
    return () => {
      mounted = false;
      unsubscribe();
      appStateSub.remove();
    };
  }, []);

  const applyDecision = useCallback(async (decision: VibeClaimDecision, credential?: VibeClaimCredential) => {
    if (decision.clearPending && credential) await clearPendingVibeClaim(credential);
    if (decision.navigateTo) {
      notifyDayVibesChanged();
      if (navigationRef.isReady()) navigationRef.navigate(decision.navigateTo);
    }
    const next = decision.notice;
    if (!next) return;
    if (next.kind === 'added') {
      // Results are matched at once from 13:00; before that they unlock at 13:00.
      showTonyteToast(t('vibeClaim.addedToast'), 'success', next.resultsReady ? undefined : t('vibeClaim.unlocksAt13'));
      return;
    }
    setNotice(next);
  }, [t]);

  const runClaim = useCallback(async (
    request: VibeClaimCredential | { method: 'verified_email' },
    pendingCredential?: VibeClaimCredential,
  ) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const result = await claimVibeWebSession(request);
      lastTransientFailureAt.current = 0;
      await applyDecision(decideFromResult(result), pendingCredential);
    } catch (error) {
      const decision = decideFromError(error);
      if (!decision.clearPending) lastTransientFailureAt.current = Date.now();
      if (__DEV__) console.log('[Tonyte][VibeClaim] claim failed', (error as { reason?: string }).reason ?? String(error));
      await applyDecision(decision, pendingCredential);
    } finally {
      inFlight.current = false;
    }
  }, [applyDecision]);

  // Pending link / referrer / code → claim as soon as the account is ready.
  useEffect(() => {
    if (!ready || !userId || !pending) return;
    if (lastTransientFailureAt.current && Date.now() - lastTransientFailureAt.current < RETRY_AFTER_MS) return;
    void runClaim(pending.credential, pending.credential);
  }, [ready, userId, pending, foregroundTick, runClaim]);

  // "Save my answers": answers verified on the web for THIS account's
  // verified email. Never claimed silently — the user confirms first.
  useEffect(() => {
    if (!ready || !userId || pending || emailCheckedFor.current === userId) return;
    const user = auth.currentUser;
    const email = user?.email?.toLowerCase();
    if (!user || !email || !user.emailVerified) return;
    emailCheckedFor.current = userId;
    let cancelled = false;
    (async () => {
      const snap = await getDoc(doc(firestore, EMAIL_INDEX_COLLECTION, email)).catch(() => null);
      if (cancelled || !snap?.exists()) return;
      const data = snap.data() as { sessionId?: string; expiresAt?: { toMillis?: () => number } };
      if (!data.sessionId || (data.expiresAt?.toMillis?.() ?? 0) < Date.now()) return;
      const dismissed = await readDismissedEmailSessions();
      if (cancelled || dismissed.includes(data.sessionId)) return;
      setEmailOffer({ sessionId: data.sessionId, email: user.email || email });
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, userId, pending]);

  useEffect(() => {
    if (!userId) {
      emailCheckedFor.current = null;
      setEmailOffer(null);
    }
  }, [userId]);

  const dismissEmailOffer = useCallback(async () => {
    const offer = emailOffer;
    setEmailOffer(null);
    if (!offer) return;
    const dismissed = await readDismissedEmailSessions();
    await AsyncStorage.setItem(EMAIL_DISMISSED_KEY, JSON.stringify([...dismissed, offer.sessionId].slice(-20))).catch(() => {});
  }, [emailOffer]);

  const acceptEmailOffer = useCallback(() => {
    setEmailOffer(null);
    void runClaim({ method: 'verified_email' });
  }, [runClaim]);

  const closeNotice = () => setNotice(null);
  const goTo = (route: 'DayVibesAura' | 'DayVibesQuestion') => {
    setNotice(null);
    if (navigationRef.isReady()) navigationRef.navigate(route);
  };

  let modal: { title: string; message: string; icon: string; buttons: TonyteModalButton[] } | null = null;
  if (emailOffer) {
    modal = {
      title: t('vibeClaim.emailFoundTitle'),
      message: t('vibeClaim.emailFoundBody').replace('{email}', emailOffer.email),
      icon: 'mail',
      buttons: [
        { label: t('vibeClaim.notNow'), variant: 'secondary', onPress: dismissEmailOffer },
        { label: t('vibeClaim.addAnswers'), variant: 'primary', onPress: acceptEmailOffer },
      ],
    };
  } else if (notice) {
    const ok: TonyteModalButton = { label: t('vibeClaim.ok'), variant: 'secondary', onPress: closeNotice };
    switch (notice.kind) {
      case 'already_completed':
        modal = {
          title: t('vibeClaim.alreadyPlayedTitle'),
          message: t('vibeClaim.alreadyPlayedBody'),
          icon: 'check-circle',
          buttons: [ok, { label: t('vibeClaim.seeMyVibes'), variant: 'primary', onPress: () => goTo('DayVibesAura') }],
        };
        break;
      case 'round_closed':
        modal = {
          title: t('vibeClaim.roundClosedTitle').replace('{date}', formatDateKey(notice.dateKey, language)),
          message: t('vibeClaim.roundClosedBody'),
          icon: 'clock',
          buttons: [ok, { label: t('vibeClaim.playToday'), variant: 'primary', onPress: () => goTo('DayVibesQuestion') }],
        };
        break;
      case 'claimed_elsewhere':
        modal = { title: t('vibeClaim.claimedElsewhereTitle'), message: t('vibeClaim.claimedElsewhereBody'), icon: 'user-x', buttons: [ok] };
        break;
      case 'not_eligible':
        modal = { title: t('vibeClaim.notFoundTitle'), message: t('vibeClaim.notEligibleBody'), icon: 'info', buttons: [ok] };
        break;
      default:
        modal = { title: t('vibeClaim.notFoundTitle'), message: t('vibeClaim.notFoundBody'), icon: 'help-circle', buttons: [ok] };
    }
  }

  return (
    <TonyteModal
      visible={Boolean(modal)}
      title={modal?.title || ''}
      message={modal?.message}
      icon={modal?.icon}
      buttons={modal?.buttons || []}
      onDismiss={emailOffer ? dismissEmailOffer : closeNotice}
    />
  );
}
